import { Component, type ReactElement, createRef } from "react";
import { connect } from "react-redux";
import { type InjectedFormProps, SubmissionError, formValueSelector, reduxForm } from "redux-form";

import { ActionButtonIntent } from "widgets";

import ProjectsSelect from "../core/forms/ProjectsSelect.js";
import { Dialog } from "../core/overlays/Dialogs.js";
import CheckboxField, { CheckboxReduxFormField } from "../core/redux-form/CheckboxField.js";
import { getDisabledPrjCapabilityTooltipMessage, prjPrivateSavingAllowed } from "@/app-utils/capability.ts";
import { getSavedItemTypeLabel, pluralizeWord } from "@/app-utils/string.ts";
import type { Project, SavedItem, SavedItemsListing } from "@/graphql.ts";
import { getProjectLabel } from "@/redux/modules/config.ts";
import { copyFlags, validateFlagsAgainstProjectUserGroup } from "@/redux/modules/flag.ts";
import { copyGroups, validateSavedGroupsAgainstProjectUserGroup } from "@/redux/modules/saved-group.ts";
import { copyViews, validateSavedViewsAgainstProjectUserGroup } from "@/redux/modules/saved-view.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { type ReduxDispatch } from "@/redux/redux-utils.ts";
import { CAPABILITY, ENTITY_TYPE } from "@/settings/settings.js";
import { SelForm } from "@/ui-modules/core/form/layout/Form.js";
import { GROUP_ENTITY } from "@/ui-modules/core/save-dialog/utils.ts";

interface ICopyItemsDialogOwnProps {
  itemsData: SavedItemsListing;
  items: SavedItem[];
  selectedIds: number[];
  projectId: number;

  onApply: () => void;
  onCancel: () => void;
}

interface ICopyItemsDialogAppStateProps {
  // data mapped from the redux store
  selectedProject: Project;
  isPrivateWorkspace?: boolean; // make private
  privateSavingAllowed: boolean;
  initialValues: Partial<FormData>;
}

interface ICopyItemsDialogDispatchProps {
  copyGroups?: (groupIds: number[], groupType: string, project: Project, isPrivate: boolean) => Promise<boolean>;
  copyViews?: (viewIds: number[], project: Project, isPrivate: boolean) => Promise<boolean>;
  copyFlags?: (flagIds: number[], project: Project, isPrivate: boolean) => Promise<boolean>;
}

export interface ICopyItemsDialogProps
  extends ICopyItemsDialogAppStateProps,
    ICopyItemsDialogDispatchProps,
    ICopyItemsDialogOwnProps,
    InjectedFormProps<FormData, any> {}

function mapStateToProps(state: IAppState, _ownProps: ICopyItemsDialogProps): ICopyItemsDialogAppStateProps {
  const selectedProject: Project = formValueSelector(CopyItemsDialogImpl.FORM_NAME)(
    state,
    CopyItemsDialogImpl.FIELD_DESTINATION,
  ); // is of Object type

  return {
    selectedProject,
    isPrivateWorkspace: selectedProject && selectedProject.isPrivateWorkspace,
    privateSavingAllowed: selectedProject && prjPrivateSavingAllowed(selectedProject),
    initialValues: {
      [CopyItemsDialogImpl.FIELD_MAKEPRIVATE]: false,
    },
  };
}

function mapDispatchToProps(
  dispatch: ReduxDispatch,
  ownProps: ICopyItemsDialogOwnProps,
): ICopyItemsDialogDispatchProps {
  return {
    copyGroups: (groupIds: number[], _groupType: string, project: Project, isPrivate: boolean) =>
      dispatch(copyGroups(groupIds, project, isPrivate, ownProps.itemsData)),
    copyViews: (viewIds: number[], project: Project, isPrivate: boolean) =>
      dispatch(copyViews(viewIds, project, isPrivate)),
    copyFlags: (flagIds: number[], project: Project, isPrivate: boolean) =>
      dispatch(copyFlags(flagIds, project, isPrivate)),
  };
}

class CopyItemsDialogImpl extends Component<ICopyItemsDialogProps> {
  public static FORM_NAME = "copyItems";

  public static FIELD_MAKEPRIVATE = "makePrivate";
  public static FIELD_DESTINATION = "destination";

  private firstTabRef = createRef<HTMLDivElement>();

  componentDidUpdate(prevProps: ICopyItemsDialogProps) {
    if (
      this.props.isPrivateWorkspace !== prevProps.isPrivateWorkspace ||
      this.props.privateSavingAllowed !== prevProps.privateSavingAllowed
    ) {
      if (this.props.isPrivateWorkspace) {
        // if the Private Workspace has been selected, we set the "make private" checkbox accordingly
        this.props.change(CopyItemsDialogImpl.FIELD_MAKEPRIVATE, true);
      } else if (!this.props.privateSavingAllowed) {
        this.props.change(CopyItemsDialogImpl.FIELD_MAKEPRIVATE, false);
      } else if (prevProps.isPrivateWorkspace) {
        // if we select something else after the Private Workspace was selected then we uncheck the "Private"
        // checkbox to have the default behaviour again
        this.props.change(CopyItemsDialogImpl.FIELD_MAKEPRIVATE, false);
      }
    }

    if (prevProps.selectedProject == undefined && this.props.selectedProject != undefined) {
      // First init as the project will be set to the last used target.
      // Need to be async or it ends up to an infinite loop
      setTimeout(() => {
        this.props.asyncValidate();
      }, 0);
    }
  }

  handleSubmitData = (values: FormData): Promise<boolean> => {
    let copyItemsPromise: Promise<boolean> = null;

    const destProject: Project = values[CopyItemsDialogImpl.FIELD_DESTINATION];
    const isPrivate = !!values[CopyItemsDialogImpl.FIELD_MAKEPRIVATE];
    const entityIds: number[] = this.props.selectedIds;

    const itemType: string = this.props.itemsData.type;
    if (itemType === ENTITY_TYPE.VIEW) {
      copyItemsPromise = this.props.copyViews(entityIds, destProject, isPrivate);
    } else if (itemType === ENTITY_TYPE.REGION_FLAG) {
      copyItemsPromise = this.props.copyFlags(entityIds, destProject, isPrivate);
    } else {
      const groupType: string = this.getGroupType();
      copyItemsPromise = this.props.copyGroups(entityIds, groupType, destProject, isPrivate);
    }
    return copyItemsPromise.then(
      (fulfilled) => {
        this.props.onApply();
        this.props.reset();
        return fulfilled;
      },
      // the following will throw an error that redux-form understands as a submission error,
      // with a general error that will be available from this.props.error
      (rejected) => {
        throw new SubmissionError({ _error: rejected.message });
      },
    );
  };

  static getListCategory(itemsData: SavedItemsListing): string {
    const itemType: string = itemsData.type;
    return itemType === ENTITY_TYPE.VIEW || itemType === ENTITY_TYPE.REGION_FLAG ? itemType : GROUP_ENTITY;
  }

  getGroupType(): string {
    return this.props.itemsData.name;
  }

  render(): ReactElement {
    const { itemsData } = this.props;

    // view | gene | ... => view | group         <containerType>
    const category: string = getSavedItemTypeLabel(itemsData);
    const itemCount: number = this.props.selectedIds.length;
    // "gene groups" | "views"
    const typeLabel: string =
      (category === GROUP_ENTITY ? this.getGroupType() + " " : "") + pluralizeWord(category, itemCount);
    const destinationTitle: string = "Copy " + itemCount + " " + typeLabel + " to ";

    const privateFieldLabel = `Private (Hidden from other ${getProjectLabel(false, false)} members)`;

    let toolTipTextForIsPrivate: string = privateFieldLabel;
    if (this.props.isPrivateWorkspace) {
      toolTipTextForIsPrivate = "Items in the Private Workspace are never shared with other users.";
    } else if (!this.props.privateSavingAllowed) {
      toolTipTextForIsPrivate = getDisabledPrjCapabilityTooltipMessage(CAPABILITY.PRIVATE_SAVING);
    }

    return (
      <Dialog
        closeFn={this.props.onCancel}
        mainActionButtonProps={{
          isLoading: !!this.props.submitting || !!this.props.asyncValidating,
          isDisabled: !!this.props.submitting || this.props.invalid || !!this.props.asyncValidating,
          label: "Copy",
          onPress: this.props.handleSubmit(this.handleSubmitData),
          intent: ActionButtonIntent.Primary,
        }}
        footerMessage={{ message: this.props.error }}
      >
        <SelForm>
          <ProjectsSelect
            name={CopyItemsDialogImpl.FIELD_DESTINATION}
            onInitToLastTarget={this.props.change}
            title={destinationTitle}
            ref={this.firstTabRef}
          />

          <CheckboxReduxFormField
            name={CopyItemsDialogImpl.FIELD_MAKEPRIVATE}
            label={privateFieldLabel}
            disabled={this.props.isPrivateWorkspace || !this.props.privateSavingAllowed}
            tooltipText={toolTipTextForIsPrivate}
            component={CheckboxField}
            style={{ marginTop: 0 }}
          />
        </SelForm>
      </Dialog>
    );
  }

  static async asyncValidate(values, dispatch, props: ICopyItemsDialogProps) {
    await CopyItemsDialogImpl.userGroupValidation(values, dispatch, props);
  }

  static userGroupValidation = (values, dispatch, props: ICopyItemsDialogProps): Promise<boolean> => {
    if (!values?.[CopyItemsDialogImpl.FIELD_DESTINATION]) {
      return Promise.resolve(true);
    }

    const selectedProject: Project = values[CopyItemsDialogImpl.FIELD_DESTINATION];
    const entityType: string = CopyItemsDialogImpl.getListCategory(props.itemsData);

    if (entityType === GROUP_ENTITY) {
      return dispatch(validateSavedGroupsAgainstProjectUserGroup(selectedProject.id, props.selectedIds)).then(
        (valid: boolean) => {
          if (!valid) {
            throw {
              _error: `One or more groups cannot be copied to '${
                selectedProject.name
              }' due to user groups  Please select a different ${getProjectLabel(false, false)}.`,
            };
          }
        },
      );
    } else if (entityType === ENTITY_TYPE.VIEW) {
      return dispatch(validateSavedViewsAgainstProjectUserGroup(selectedProject.id, props.selectedIds)).then(
        (valid: boolean) => {
          if (!valid) {
            throw {
              _error: `One or more views cannot be copied to '${
                selectedProject.name
              }' due to user groups  Please select a different ${getProjectLabel(false, false)}.`,
            };
          }
        },
      );
    } else if (entityType === ENTITY_TYPE.REGION_FLAG) {
      return dispatch(validateFlagsAgainstProjectUserGroup(selectedProject.id, props.selectedIds)).then(
        (valid: boolean) => {
          if (!valid) {
            throw {
              _error: `One or more flags cannot be copied to '${
                selectedProject.name
              }' due to user groups  Please select a different ${getProjectLabel(false, false)}.`,
            };
          }
        },
      );
    } else {
      return Promise.resolve(true); // e.g. files can be copied, no link to any user group
    }
  };
}

const CopyItemsDialog = connect<ICopyItemsDialogAppStateProps, ICopyItemsDialogDispatchProps, ICopyItemsDialogOwnProps>(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm({
    form: CopyItemsDialogImpl.FORM_NAME,
    asyncValidate: CopyItemsDialogImpl.asyncValidate,
    shouldAsyncValidate: (_params) => true,
  })(CopyItemsDialogImpl as any) as any,
);

export { CopyItemsDialog };
